import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vitalsService, UpdateVitalSignsDto } from '../vitalsService';

export function useVitals(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['vitals', page, limit, search],
    queryFn: () => vitalsService.getVitals(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useVitalsByPatient(patientId: string, page = 1, limit = 50) {
  return useQuery({
    queryKey: ['vitals', 'patient', patientId, page, limit],
    queryFn: () => vitalsService.getVitalsByPatient(patientId, page, limit),
    placeholderData: (previous) => previous,
    enabled: !!patientId,
  });
}

export function useCreateVitals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vitalsService.createVitals,
    meta: { successMessage: 'Vital signs recorded' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vitals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateVitals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVitalSignsDto }) =>
      vitalsService.updateVitals(id, data),
    meta: { successMessage: 'Vital signs updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vitals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteVitals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vitalsService.deleteVitals(id),
    meta: { successMessage: 'Vital signs deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vitals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useBulkDeleteVitals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => vitalsService.bulkDeleteVitals(ids),
    meta: { successMessage: 'Selected vital records deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vitals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
